import * as React from 'react';
import { useRouter } from 'next/router';
import Link, { LinkProps } from 'next/link';
import trackGTM, { TrackingObject } from '@mybet/core/utils/trackGTM';

export type RouterLinkProps = {
  external?: boolean;
  onClick?(e?: React.SyntheticEvent<HTMLAnchorElement, MouseEvent>): void;
  trackingObject?: TrackingObject;
  children?: React.ReactNode;
  style?: Record<string, number | string>;
} & LinkProps;

type RouterLinkRef = HTMLAnchorElement;

const RouterLink = React.forwardRef<
  RouterLinkRef,
  React.PropsWithChildren<RouterLinkProps>
>(function AppBar(props, ref) {
  const {
    href,
    children,
    external,
    replace,
    scroll,
    shallow,
    trackingObject,
    onClick,
    ...other
  } = props;
  const { locale, asPath } = useRouter();
  const isExternalHref = typeof href === 'string' && /^https?:\/\//.test(href);

  const handleClick = (
    e: React.SyntheticEvent<HTMLAnchorElement, MouseEvent>
  ): void => {
    if (trackingObject) {
      trackGTM(
        { ...trackingObject, label: trackingObject.label || asPath },
        locale
      );
    }
    if (onClick) {
      onClick(e);
    }
  };

  // Render as a regular `a` tag if external link.
  if (external || isExternalHref) {
    return (
      <a
        href={typeof href === 'string' ? href : undefined}
        rel="noopener noreferrer"
        target="_blank"
        ref={ref}
        onClick={handleClick}
        {...other}
      >
        {children}
      </a>
    );
  }

  return (
    <Link
      href={href}
      replace={replace}
      scroll={scroll}
      shallow={shallow}
      passHref
    >
      <a ref={ref} onClick={handleClick} {...other}>
        {children}
      </a>
    </Link>
  );
});

export default React.memo(RouterLink);
